import { useState } from 'react';
import { useRecurringExpenses, type RecurringExpense, type RecurringExpenseInput } from '../../hooks/useRecurringExpenses';
import {
    Plus, Trash2, Edit, ToggleLeft, ToggleRight,
    Calendar, RefreshCw, Loader2, X
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { localDate } from '../../lib/periods';

const frequencyLabels: Record<RecurringExpense['frequency'], string> = {
    weekly: 'Weekly',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly'
};

const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);

export const RecurringExpensesManager = () => {
    const { expenses, loading, monthlyTotal, createExpense, updateExpense, deleteExpense, toggleActive } = useRecurringExpenses();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingExpense, setEditingExpense] = useState<RecurringExpense | null>(null);

    const handleEdit = (expense: RecurringExpense) => {
        setEditingExpense(expense);
        setIsModalOpen(true);
    };

    const handleClose = () => {
        setIsModalOpen(false);
        setEditingExpense(null);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-accent" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-fg">Recurring Expenses</h2>
                    <p className="text-sm text-fg-3">
                        Est. Monthly: <span className="font-mono text-accent">{formatCurrency(monthlyTotal)}</span>
                    </p>
                </div>
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 fd-btn-primary rounded-lg font-medium transition-opacity"
                >
                    <Plus className="w-4 h-4" />
                    Add Recurring
                </button>
            </div>

            {/* Empty State */}
            {expenses.length === 0 ? (
                <div className="text-center py-12 bg-panel rounded-xl border border-line">
                    <RefreshCw className="w-10 h-10 mx-auto mb-3 text-fg-3 opacity-50" />
                    <p className="text-fg-2">No recurring expenses yet</p>
                    <p className="text-sm text-fg-3 mt-1">Add subscriptions, rent, or other regular costs</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {expenses.map((expense) => (
                        <div
                            key={expense.id}
                            className={`p-4 bg-panel rounded-xl border border-line flex items-center gap-4 transition-opacity ${!expense.is_active ? 'opacity-50' : ''}`}
                        >
                            {/* Toggle */}
                            <button
                                onClick={() => toggleActive(expense.id, !expense.is_active)}
                                className="text-fg-3 hover:text-accent transition-colors"
                                title={expense.is_active ? 'Pause' : 'Activate'}
                            >
                                {expense.is_active ? (
                                    <ToggleRight className="w-6 h-6 text-success" />
                                ) : (
                                    <ToggleLeft className="w-6 h-6" />
                                )}
                            </button>

                            {/* Details */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-fg truncate">{expense.vendor}</span>
                                    <span className="text-xs px-2 py-0.5 bg-raised rounded-full text-fg-3">
                                        {frequencyLabels[expense.frequency]}
                                    </span>
                                    {expense.auto_post && (
                                        <span className="text-xs px-2 py-0.5 bg-success/10 text-success rounded-full">
                                            Auto
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-fg-3 flex items-center gap-2 mt-0.5">
                                    <Calendar className="w-3 h-3" />
                                    Next: {format(new Date(expense.next_due_date), 'dd MMM yyyy')}
                                    <span className="text-[10px]">
                                        ({formatDistanceToNow(new Date(expense.next_due_date), { addSuffix: true })})
                                    </span>
                                </p>
                            </div>

                            {/* Amount */}
                            <div className="text-right">
                                <p className="font-mono font-bold text-fg">
                                    {formatCurrency(expense.amount)}
                                </p>
                                {expense.category && (
                                    <p className="text-xs text-fg-3">{expense.category}</p>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => handleEdit(expense)}
                                    className="p-2 hover:bg-raised rounded-lg transition-colors"
                                >
                                    <Edit className="w-4 h-4 text-fg-3" />
                                </button>
                                <button
                                    onClick={() => deleteExpense(expense.id)}
                                    className="p-2 hover:bg-danger/10 rounded-lg transition-colors"
                                >
                                    <Trash2 className="w-4 h-4 text-danger" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <RecurringExpenseModal
                    expense={editingExpense}
                    onClose={handleClose}
                    onCreate={createExpense}
                    onUpdate={updateExpense}
                />
            )}
        </div>
    );
};

// --- MODAL COMPONENT ---
interface ModalProps {
    expense: RecurringExpense | null;
    onClose: () => void;
    onCreate: (input: RecurringExpenseInput) => Promise<any>;
    onUpdate: (id: string, input: Partial<RecurringExpenseInput>) => Promise<any>;
}

const RecurringExpenseModal = ({ expense, onClose, onCreate, onUpdate }: ModalProps) => {
    const [form, setForm] = useState<RecurringExpenseInput>({
        vendor: expense?.vendor || '',
        description: expense?.description || '',
        category: expense?.category || 'Subscription',
        amount: expense?.amount || 0,
        vat_amount: expense?.vat_amount || 0,
        frequency: expense?.frequency || 'monthly',
        next_due_date: expense?.next_due_date || localDate(new Date()),
        auto_post: expense?.auto_post || false,
        notes: expense?.notes || ''
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);

        if (expense) {
            await onUpdate(expense.id, form);
        } else {
            await onCreate(form);
        }

        setSaving(false);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-scrim/50 backdrop-blur-sm">
            <div className="w-full max-w-md bg-panel rounded-xl border border-line shadow-2xl">
                <div className="flex items-center justify-between p-4 border-b border-line">
                    <h3 className="font-bold text-fg">
                        {expense ? 'Edit Recurring Expense' : 'Add Recurring Expense'}
                    </h3>
                    <button onClick={onClose} className="p-1 hover:bg-raised rounded">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-fg-3 uppercase mb-1">Vendor</label>
                        <input
                            type="text"
                            value={form.vendor}
                            onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                            className="w-full px-3 py-2 bg-raised border border-line rounded-lg text-fg"
                            placeholder="e.g. Microsoft 365"
                            required
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-fg-3 uppercase mb-1">Amount</label>
                            <input
                                type="number"
                                step="0.01"
                                value={form.amount}
                                onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) })}
                                className="w-full px-3 py-2 bg-raised border border-line rounded-lg text-fg"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-fg-3 uppercase mb-1">Frequency</label>
                            <select
                                value={form.frequency}
                                onChange={(e) => setForm({ ...form, frequency: e.target.value as any })}
                                className="w-full px-3 py-2 bg-raised border border-line rounded-lg text-fg"
                            >
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                                <option value="quarterly">Quarterly</option>
                                <option value="yearly">Yearly</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-fg-3 uppercase mb-1">Next Due Date</label>
                        <input
                            type="date"
                            value={form.next_due_date}
                            onChange={(e) => setForm({ ...form, next_due_date: e.target.value })}
                            className="w-full px-3 py-2 bg-raised border border-line rounded-lg text-fg"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-fg-3 uppercase mb-1">Category</label>
                        <input
                            type="text"
                            value={form.category}
                            onChange={(e) => setForm({ ...form, category: e.target.value })}
                            className="w-full px-3 py-2 bg-raised border border-line rounded-lg text-fg"
                            placeholder="e.g. Software, Insurance, Rent"
                        />
                    </div>

                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.auto_post}
                            onChange={(e) => setForm({ ...form, auto_post: e.target.checked })}
                            className="w-4 h-4 rounded border-line"
                        />
                        <span className="text-sm text-fg-2">Auto-post to ledger when due</span>
                    </label>

                    <button
                        type="submit"
                        disabled={saving}
                        className="w-full py-3 fd-btn-primary rounded-lg font-bold transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                        {expense ? 'Update' : 'Create'} Recurring Expense
                    </button>
                </form>
            </div>
        </div>
    );
};
